// Platform-level module and workspace definitions shared by the API and the web app.
//
// A "platform module" is what a tenant admin switches on for an organisation
// (Enforce, Track, Scan, Intelligence, BIIC). Track and Scan are dormant
// (`hidden`): kept so stored ids still resolve, never offered. Each maps onto one or more M_*
// permission module codes that already drive RBAC and `tenant_modules`.
//
// A "workspace profile" is what a user picks for themselves during onboarding.
// It never grants permissions — the user's DB role does that — it only decides
// which navigation pills, home screen and quick actions are composed.

export type PlatformModuleId = 'enforce' | 'track' | 'scan' | 'intel' | 'biic';

export interface PlatformModule {
  id: PlatformModuleId;
  code: string;
  name: string;
  color: string;
  origin: string;
  blurb: string;
  you: string;
  features: string[];
  /** M_* codes toggled in tenant_modules when this platform module is enabled. */
  moduleCodes: string[];
  /** Dormant: never offered in the UI or composed into a nav. Kept so stored ids and VALID_MODULE_IDS still resolve. */
  hidden?: true;
}

export const PLATFORM_MODULES: PlatformModule[] = [
  {
    id: 'enforce', code: 'Enforce', name: 'Illicit alcohol enforcement', color: '#d63a44', origin: 'From Kanvara',
    blurb: 'Field capture with AI label reading, tiered review queue, cases with SLAs closed with a recorded outcome, and the public tip-off portal.',
    you: 'You photograph products, review detections or run cases.',
    features: ['Field verification + sweep', 'AI detection tiers A–D', 'Quality assurance', 'CMS + SLA', 'Close with outcome', 'Tip-offs', 'Product registry', 'Analytics + reports'],
    moduleCodes: ['M_CAP', 'M_FIELD', 'M_REV', 'M_CASE', 'M_CMD', 'M_REG', 'M_MDASH'],
  },
  {
    id: 'track', code: 'Track', name: 'Asset fleet tracking', color: '#2a7fb0', origin: 'From Arctic Eye',
    blurb: 'Every branded asset on a map with geofences, breach alerts, temperature and power telemetry, maintenance tasks and merchant contracts.',
    you: 'You manage assets, merchants, alerts or maintenance.',
    features: ['Fleet map + list', 'Geofences (circle / polygon)', 'Breach + power + temp alerts', 'Maintenance tasks + SLAs', 'Merchants + contracts', 'Bulk Excel upload', 'Revenue + ROI reports'],
    moduleCodes: ['M_TRACK'],
    hidden: true,
  },
  {
    id: 'scan', code: 'Scan', name: 'Encrypted NFC authentication', color: '#2a9a4b', origin: 'From Kanvara Scan',
    blurb: 'Tags applied on the manufacturing line carry a hashed, encrypted payload only the Kanvara app can read. Each tap logs place and time.',
    you: 'You verify bottles, manage tag batches or watch clone alerts.',
    features: ['Tap to verify (Genuine / Unknown / Seen elsewhere)', 'Tag batch registry', 'Clone + diversion alerts', 'Tap history per outlet', 'Consumer verification mode'],
    moduleCodes: ['M_TAGS'],
    hidden: true,
  },
  {
    id: 'intel', code: 'Intelligence', name: 'Fused intelligence', color: '#2f7a7e', origin: 'New layer',
    blurb: 'Entity resolution across captures, cases and BIIC leads. Network graph, excise exposure model and court-ready dossiers.',
    you: 'You investigate networks or report to SARS and SAPS.',
    features: ['Suspect network graph', 'Outlet risk map', 'Excise exposure (SARS)', 'Case hierarchy', 'Dossier export'],
    moduleCodes: ['M_INTEL'],
  },
  {
    id: 'biic', code: 'Trade', name: 'Trade Intelligence', color: '#d99a3a', origin: 'Brand Integrity Intelligence Centre',
    blurb: 'National, cross-sector fusion view. Graded leads (Admiralty Code), Threat × Confidence scoring, corridor risk, online seller clusters, eight-stage case lifecycle and a double-count-proof seizure ledger.',
    you: 'You run the CIA desk: grade sources, verify leads, brief SAPS, SARS and brand owners.',
    features: ['Lead intake + Admiralty grading', 'Threat × Confidence model', 'Provincial risk heat grid', 'Trade corridor risk', 'Online / social seller clusters', '8-stage case lifecycle + escalation matrix', 'Evidence-class tagging', 'Seizure ledger (parent / child)'],
    moduleCodes: ['M_BIIC'],
  },
];

export const PLATFORM_MODULE_BY_ID: Record<PlatformModuleId, PlatformModule> = Object.fromEntries(
  PLATFORM_MODULES.map(m => [m.id, m]),
) as Record<PlatformModuleId, PlatformModule>;

/** Modules the UI offers. Hidden modules stay in PLATFORM_MODULES so stored ids still resolve. */
export const VISIBLE_PLATFORM_MODULES: PlatformModule[] = PLATFORM_MODULES.filter(m => !m.hidden);

/** Which platform modules are on, given the set of enabled M_* codes for a tenant. */
export function platformModulesFromCodes(enabledCodes: Iterable<string>): PlatformModuleId[] {
  const set = new Set(enabledCodes);
  return PLATFORM_MODULES.filter(m => !m.hidden && m.moduleCodes.some(c => set.has(c))).map(m => m.id);
}

export type ApprovalLevel = 'Instant' | 'Admin approves' | 'Exec approves';

export type WorkspaceProfileId =
  | 'field' | 'investigator' | 'biicinv' | 'analyst' | 'fleet' | 'tech' | 'brand' | 'gov' | 'cia' | 'ciam' | 'sars';

export interface WorkspaceProfile {
  id: WorkspaceProfileId;
  name: string;
  initials: string;
  desc: string;
  /** Platform modules that must all be tenant-enabled AND user-chosen for this profile to be offered. */
  needs: PlatformModuleId[];
  approval: ApprovalLevel;
  /** Nav labels in order; first one is home. Resolved through NAV_LIBRARY. */
  nav: string[];
  home: string;
  eyebrow: string;
  title: string;
  /** DB role that carries this profile's permissions. */
  dbRole: string;
  /** Dormant: never offered. Kept so stored workspace_json still resolves. */
  hidden?: true;
}

export const WORKSPACE_PROFILES: WorkspaceProfile[] = [
  { id: 'field', name: 'Field agent', initials: 'FA', desc: 'High-volume capture at outlets. Own history and leaderboard.', needs: ['enforce'], approval: 'Instant', nav: ['Field verification', 'History', 'My Cases', 'Support'], home: 'Field verification', eyebrow: 'Field', title: 'Ready to verify', dbRole: 'reporter' },
  { id: 'investigator', name: 'Investigator', initials: 'IN', desc: 'Owns assigned cases, attaches evidence, links related captures and leads, hands over to authorities.', needs: ['enforce'], approval: 'Admin approves', nav: ['Operational intelligence', 'Field verification', 'CMS', 'Management review', 'Notifications', 'History', 'Legal library', 'Support'], home: 'CMS', eyebrow: 'Investigator', title: 'Case management system', dbRole: 'investigator' },
  { id: 'biicinv', name: 'Investigator', initials: 'IN', desc: 'Verifies in the field, works the quality assurance queue, opens and closes cases, and reads the AI cross-reference on every scan.', needs: ['enforce', 'biic'], approval: 'Admin approves', nav: ['Trade Intelligence', 'Operational intelligence', 'Field verification', 'Quality assurance', 'CMS', 'Management review', 'Legal library', 'Support'], home: 'Trade Intelligence', eyebrow: 'BIIC Investigator', title: 'Scan to case', dbRole: 'biic_investigator' },
  { id: 'analyst', name: 'Operations analyst', initials: 'OA', desc: 'Triages the quality assurance queue, opens cases, runs analytics and reports.', needs: ['enforce'], approval: 'Admin approves', nav: ['Quality assurance', 'CMS', 'Management review', 'Dashboard', 'Analytics', 'Reports', 'Legal library', 'Support'], home: 'Quality assurance', eyebrow: 'Operations', title: 'Quality assurance', dbRole: 'investigator' },
  { id: 'fleet', name: 'Fleet manager', initials: 'FM', desc: 'Watches assets, geofences and alerts; assigns maintenance; manages merchants and contracts.', needs: ['track'], approval: 'Admin approves', nav: ['Fleet', 'Alerts', 'Maintenance', 'Merchants', 'Reports', 'Support'], home: 'Fleet', eyebrow: 'Fleet', title: 'Cooler Fleet', dbRole: 'fleet_manager', hidden: true },
  { id: 'tech', name: 'Field technician', initials: 'FT', desc: 'Receives maintenance tasks, logs work with photos and time, moves units with a reason.', needs: ['track'], approval: 'Instant', nav: ['My Tasks', 'Fleet', 'History', 'Support'], home: 'My Tasks', eyebrow: 'Technician', title: 'Maintenance Tasks', dbRole: 'field_technician', hidden: true },
  { id: 'brand', name: 'Brand / manufacturer lead', initials: 'BL', desc: 'Brand-scoped dashboard, product registry, tag batch issuance and vulnerability reports.', needs: ['scan'], approval: 'Admin approves', nav: ['Brand portal', 'Registry', 'Tag Batches', 'Dashboard', 'Reports', 'Support'], home: 'Brand portal', eyebrow: 'Manufacturer', title: 'Brand Registry', dbRole: 'brand_protection_officer', hidden: true },
  { id: 'gov', name: 'Compliance reviewer', initials: 'CR', desc: 'Reviews closed cases and outcomes, reads the dashboard and reports.', needs: ['enforce'], approval: 'Exec approves', nav: ['CMS', 'Management review', 'Dashboard', 'Reports', 'Legal library', 'Support'], home: 'CMS', eyebrow: 'Compliance', title: 'Case Outcomes', dbRole: 'governance' },
  { id: 'cia', name: 'Analyst (Trade Intelligence)', initials: 'AN', desc: 'Logs and grades leads, runs verification, advances cases through the lifecycle, maintains the audit trail.', needs: ['biic'], approval: 'Admin approves', nav: ['Trade Intelligence', 'Operational intelligence', 'CMS', 'Leads', 'Reports', 'Legal library', 'Support'], home: 'Trade Intelligence', eyebrow: 'Analyst', title: 'Trade Intelligence', dbRole: 'biic_analyst' },
  { id: 'ciam', name: 'Senior case manager (Trade Intelligence)', initials: 'CM', desc: 'Owns the escalation matrix, opens formal case files, coordinates enforcement support with SAPS, SARS and BMA.', needs: ['biic', 'intel'], approval: 'Exec approves', nav: ['Trade Intelligence', 'Operational intelligence', 'CMS', 'Reports', 'Legal library', 'Support'], home: 'Trade Intelligence', eyebrow: 'Case Manager', title: 'Escalations and Case Files', dbRole: 'biic_case_manager' },
  { id: 'sars', name: 'Revenue liaison (SARS)', initials: 'RL', desc: 'Read-only intelligence: excise exposure by province and brand, seizure volumes, exportable evidence.', needs: ['intel'], approval: 'Exec approves', nav: ['Operational intelligence', 'Excise', 'Reports', 'Support'], home: 'Operational intelligence', eyebrow: 'Revenue Liaison', title: 'Excise Exposure', dbRole: 'sars_official' },
];

export const WORKSPACE_PROFILE_BY_ID: Record<string, WorkspaceProfile> = Object.fromEntries(
  WORKSPACE_PROFILES.map(p => [p.id, p]),
);

/** Profiles the UI offers. Hidden profiles stay in WORKSPACE_PROFILES so stored workspace_json still resolves. */
export const VISIBLE_WORKSPACE_PROFILES: WorkspaceProfile[] = WORKSPACE_PROFILES.filter(p => !p.hidden);

export interface NavLibraryEntry {
  path: string;
  /** Platform module that must be enabled for the tenant and chosen by the user. Undefined = always available. */
  platformModule?: PlatformModuleId;
  /** M_* code the user's role must be able to read. Undefined = no permission gate beyond login. */
  moduleCode?: string;
}

/** Nav label → route. Labels are the ones used in WORKSPACE_PROFILES.nav. */
export const NAV_LIBRARY: Record<string, NavLibraryEntry> = {
  'Dashboard':    { path: '/dashboard', platformModule: 'enforce', moduleCode: 'M_CMD' },
  // 29 Sep 2026 working session: "Capture" → "Field verification", "Review" → "Quality assurance",
  // "Cases" → "CMS" (case management system), "Audit" → "Management review". Routes unchanged.
  'Field verification': { path: '/capture', platformModule: 'enforce', moduleCode: 'M_CAP' },
  'History':      { path: '/history', platformModule: 'enforce', moduleCode: 'M_CAP' },
  'My Cases':     { path: '/worklist', platformModule: 'enforce', moduleCode: 'M_CASE' },
  'CMS':          { path: '/worklist', platformModule: 'enforce', moduleCode: 'M_CASE' },
  'Management review': { path: '/audit', platformModule: 'enforce', moduleCode: 'M_CASE' },
  // Investigator feed: allocations, court dates and postponements, approvals (29 Sep 2026).
  'Notifications': { path: '/notifications', platformModule: 'enforce', moduleCode: 'M_CASE' },
  'Quality assurance': { path: '/review', platformModule: 'enforce', moduleCode: 'M_REV' },
  // Legacy labels kept so older profile lists and stored selections still resolve.
  'Capture':      { path: '/capture', platformModule: 'enforce', moduleCode: 'M_CAP' },
  'Cases':        { path: '/worklist', platformModule: 'enforce', moduleCode: 'M_CASE' },
  'Audit':        { path: '/audit', platformModule: 'enforce', moduleCode: 'M_CASE' },
  'Review':       { path: '/review', platformModule: 'enforce', moduleCode: 'M_REV' },
  'Review Queue': { path: '/review', platformModule: 'enforce', moduleCode: 'M_REV' },
  'Analytics':    { path: '/analytics', platformModule: 'enforce', moduleCode: 'M_CMD' },
  'Reports':      { path: '/reports' },
  'Registry':     { path: '/registry', platformModule: 'enforce', moduleCode: 'M_REG' },
  // Dormant: Track and Scan are hidden modules, so composeNav never emits these.
  // Kept only because the hidden fleet / tech / brand profiles still list them.
  'Fleet':        { path: '/fleet', platformModule: 'track', moduleCode: 'M_TRACK' },
  'Alerts':       { path: '/fleet/alerts', platformModule: 'track', moduleCode: 'M_TRACK' },
  'Maintenance':  { path: '/fleet/maintenance', platformModule: 'track', moduleCode: 'M_TRACK' },
  'My Tasks':     { path: '/fleet/maintenance', platformModule: 'track', moduleCode: 'M_TRACK' },
  'Merchants':    { path: '/fleet/merchants', platformModule: 'track', moduleCode: 'M_TRACK' },
  'Tag Batches':  { path: '/scan', platformModule: 'scan', moduleCode: 'M_TAGS' },
  // 1 Oct 2026: "Intelligence" → "Operational intelligence". The old label stays as an alias.
  'Operational intelligence': { path: '/intel', platformModule: 'intel', moduleCode: 'M_INTEL' },
  'Intelligence': { path: '/intel', platformModule: 'intel', moduleCode: 'M_INTEL' },
  'Excise':       { path: '/intel/excise', platformModule: 'intel', moduleCode: 'M_INTEL' },
  // The national, all-product view (28 Sep 2026: "call the BIIC module Trade Intelligence").
  'Trade Intelligence': { path: '/biic', platformModule: 'biic', moduleCode: 'M_BIIC' },
  'Leads':        { path: '/biic/leads', platformModule: 'biic', moduleCode: 'M_BIIC' },
  // 8 Oct 2026: statutes and sections engaged by counterfeit and illicit liquor.
  'Legal library': { path: '/legal', moduleCode: 'M_CASE' },
  // 14 Oct 2026: brand owners authorise BIIC to investigate scans that quality assurance flagged.
  'Brand portal': { path: '/brand', moduleCode: 'M_REG' },
  'Support':      { path: '/support' },
  'Admin':        { path: '/admin', moduleCode: 'M_ADMIN' },
};

/**
 * Main-nav order (owner request, 1 Oct 2026): Trade intelligence, Operational intelligence,
 * Field verification, Quality assurance, CMS, Management review, then every other pill in the
 * order its profile lists it, and Support last. Legacy aliases rank with the label they replace.
 */
const NAV_RANK: Record<string, number> = {
  'Trade Intelligence': 0,
  'Operational intelligence': 1, 'Intelligence': 1,
  'Field verification': 2, 'Capture': 2,
  'Quality assurance': 3, 'Review': 3, 'Review Queue': 3,
  'CMS': 4, 'Cases': 4,
  'Management review': 5, 'Audit': 5,
};
const NAV_RANK_OTHER = 50;
const NAV_RANK_LAST = 99;

function navRank(label: string): number {
  if (label === 'Support') return NAV_RANK_LAST;
  return NAV_RANK[label] ?? NAV_RANK_OTHER;
}

/** Stable sort of nav items (anything with a label) into the canonical main-nav order. */
export function orderNavItems<T extends { label: string }>(items: T[]): T[] {
  return items.map((it, i) => ({ it, i })).sort((a, b) => navRank(a.it.label) - navRank(b.it.label) || a.i - b.i).map(x => x.it);
}

/** Stable sort of nav labels into the canonical main-nav order. */
export function orderNavLabels(labels: string[]): string[] {
  return orderNavItems(labels.map(label => ({ label }))).map(x => x.label);
}

export interface QuickAction { label: string; sub: string; color: string; path: string }

export const QUICK_ACTIONS: Record<WorkspaceProfileId, QuickAction[]> = {
  field: [{ label: 'New verification', sub: 'Front + back label', color: '#d63a44', path: '/capture' }, { label: 'Sweep mode', sub: 'Rapid multi-product', color: '#d63a44', path: '/capture/sweep' }, { label: 'My history', sub: 'Your verifications', color: '#8f9a93', path: '/history' }],
  investigator: [{ label: 'My cases', sub: 'Open and overdue', color: '#d63a44', path: '/worklist' }, { label: 'Network', sub: 'Open intelligence graph', color: '#2f7a7e', path: '/intel/network' }, { label: 'Evidence', sub: 'Attach capture or file', color: '#d99a3a', path: '/worklist' }, { label: 'Handover', sub: 'Build SAPS pack', color: '#0d3318', path: '/intel/dossier' }],
  biicinv: [{ label: 'New verification', sub: 'Front + back label', color: '#d63a44', path: '/capture' }, { label: 'Scan intelligence', sub: 'AI cross-reference', color: '#d99a3a', path: '/biic/scans' }, { label: 'Quality assurance', sub: 'Pending detections', color: '#2a7fb0', path: '/review' }, { label: 'My cases', sub: 'Open and overdue', color: '#0d3318', path: '/worklist' }, { label: 'Management review', sub: 'Scan → QA → case trail', color: '#2f7a7e', path: '/audit' }],
  analyst: [{ label: 'Quality assurance', sub: 'Pending detections', color: '#d63a44', path: '/review' }, { label: 'New case', sub: 'From a detection', color: '#0d3318', path: '/worklist' }, { label: 'Analytics', sub: 'Trends and provinces', color: '#2a7fb0', path: '/analytics' }, { label: 'Reports', sub: 'Generate + AI summary', color: '#8f9a93', path: '/reports' }],
  fleet: [{ label: 'Fleet map', sub: 'All units', color: '#2a7fb0', path: '/fleet' }, { label: 'Alerts', sub: 'Geofence exits', color: '#d63a44', path: '/fleet/alerts' }, { label: 'Maintenance', sub: 'Open tasks', color: '#d99a3a', path: '/fleet/maintenance' }, { label: 'Merchants', sub: 'Contracts + deposits', color: '#8f9a93', path: '/fleet/merchants' }],
  tech: [{ label: 'My tasks', sub: 'Due today', color: '#d99a3a', path: '/fleet/maintenance' }, { label: 'Log work', sub: 'Photos + time', color: '#2a7fb0', path: '/fleet/maintenance' }, { label: 'Move unit', sub: 'Reason required', color: '#8f9a93', path: '/fleet' }],
  brand: [{ label: 'Registry', sub: 'Brands and SKUs', color: '#d63a44', path: '/registry' }, { label: 'Tag batches', sub: 'Issue and revoke', color: '#2a9a4b', path: '/scan' }, { label: 'Vulnerability', sub: 'Where your brand is hit', color: '#2a7fb0', path: '/analytics' }, { label: 'Reports', sub: 'Brand-scoped', color: '#8f9a93', path: '/reports' }],
  gov: [{ label: 'Closed cases', sub: 'Outcomes this month', color: '#0d3318', path: '/worklist?status=closed' }, { label: 'Cases', sub: 'All open', color: '#d63a44', path: '/worklist' }, { label: 'Reports', sub: 'Exports', color: '#8f9a93', path: '/reports' }, { label: 'Management review', sub: 'Scan → QA → case trail', color: '#2f7a7e', path: '/audit' }],
  cia: [{ label: 'Log lead', sub: 'Grade the source', color: '#d99a3a', path: '/biic/leads' }, { label: 'Verification queue', sub: 'Awaiting', color: '#2a7fb0', path: '/biic/leads' }, { label: 'Threat × Confidence', sub: 'National readout', color: '#d63a44', path: '/biic' }, { label: 'Seller clusters', sub: 'Online telemetry', color: '#2f7a7e', path: '/biic/online' }],
  ciam: [{ label: 'Escalations', sub: 'High-risk and incidents', color: '#d63a44', path: '/biic/lifecycle' }, { label: 'Case files', sub: 'Documentation checklist', color: '#d99a3a', path: '/biic/lifecycle' }, { label: 'Enforcement support', sub: 'SAPS pre-op brief', color: '#0d3318', path: '/biic/lifecycle' }, { label: 'Seizure ledger', sub: 'Parent / child check', color: '#2a9a4b', path: '/biic/ledger' }],
  sars: [{ label: 'Excise exposure', sub: 'Last 30 days', color: '#d63a44', path: '/intel/excise' }, { label: 'By province', sub: 'Untaxed litres', color: '#2f7a7e', path: '/intel/excise' }, { label: 'Export', sub: 'CSV + dossier', color: '#0d3318', path: '/reports' }],
};

export interface WorkspaceSelection {
  /** Platform modules the user chose for themselves. */
  modules: PlatformModuleId[];
  profile: WorkspaceProfileId;
}

export interface ComposedNavItem { path: string; label: string }

/** Profiles a user may pick: every needed module must be tenant-enabled and user-chosen. */
export function eligibleProfiles(tenantModules: PlatformModuleId[], userModules: PlatformModuleId[]): WorkspaceProfile[] {
  const t = new Set(tenantModules);
  const u = new Set(userModules);
  return WORKSPACE_PROFILES.filter(p => !p.hidden && p.needs.every(n => t.has(n) && u.has(n)));
}

/**
 * Compose the navigation for a user. Pure and deterministic so it can be unit-tested
 * and shared by the API (`/me`) and the shell.
 *
 * @param canRead  returns true when the user's role may read the given M_* code.
 */
export function composeNav(
  selection: WorkspaceSelection,
  tenantModules: PlatformModuleId[],
  canRead: (moduleCode: string) => boolean,
): ComposedNavItem[] {
  const profile = WORKSPACE_PROFILE_BY_ID[selection.profile];
  if (!profile) return [];
  const t = new Set(tenantModules);
  const u = new Set(selection.modules);
  const out: ComposedNavItem[] = [];
  const seen = new Set<string>();
  for (const label of orderNavLabels(profile.nav)) {
    const entry = NAV_LIBRARY[label];
    if (!entry) continue;
    if (entry.platformModule && PLATFORM_MODULE_BY_ID[entry.platformModule]?.hidden) continue;
    if (entry.platformModule && (!t.has(entry.platformModule) || !u.has(entry.platformModule))) continue;
    if (entry.moduleCode && !canRead(entry.moduleCode)) continue;
    if (seen.has(entry.path)) continue;
    seen.add(entry.path);
    out.push({ path: entry.path, label });
  }
  return out;
}

export function homePathFor(selection: WorkspaceSelection, tenantModules: PlatformModuleId[], canRead: (m: string) => boolean): string {
  const nav = composeNav(selection, tenantModules, canRead);
  // The profile's declared home wins when it survived gating; nav order no longer implies home.
  const declared = WORKSPACE_PROFILE_BY_ID[selection.profile]?.home;
  const declaredPath = declared ? NAV_LIBRARY[declared]?.path : undefined;
  if (declaredPath && nav.some(n => n.path === declaredPath)) return declaredPath;
  return nav[0]?.path ?? '/dashboard';
}
